import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormHelperText, Grid, TextField, Typography } from '@mui/material';
import { AppImageDimension, AppImages } from 'app/appImages';
import { RequestLifeCycle } from "app/appModels";
import { MprcButton, MprcContainer, MprcRequestErrorDialog, MprcRequestErrorReport, MprcRequestIndicator } from "components";
import { IconButtonClose } from "components/iconButtons";
import { AutoCompleteSuggestions } from 'components/MprcAutoComplete';
import { AutoCompleteHierarchicalModel } from 'components/MprcAutoCompleteHierarchical';
import { MprcImageSelection } from 'components/MprcImageHandler';
import { MprcSwitch } from 'components/MprcSwitch';
import {
    CreateOrUpdateListCommand,
    GetListPublicationDto,
    MemberAutoCompleteDataModelDto,
    PublicationApprovalStatus,
    PublicationCreationSettingsResponse,
    PublicationType,
    TopicAutoCompleteHierarchicalModelDto
} from "generated/CoreApi_v1";
import { useReferredState } from "hooks/useReferredState";
import LibUtils from 'lib/LibUtils';
import MediaUtils from 'lib/MediaUtils';
import { useSnackbar } from "notistack";
import { useCoreApi } from "providers/CoreApiProvider";
import { useSession } from 'providers/SessionProvider';
import * as React from 'react';
import { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { KnowledgePaths } from 'routes/KnowledgeRouter';
import { NIL } from "uuid";
import { MemberAutoComplete } from 'views/foundation/member/components/MemberAutoComplete';
import { publish } from 'views/knowledge/feed/FeedEvents';
import { ICreatePublicationProps, PublicationUploadingStatus } from "views/knowledge/publication/creation";
import {
    IInstitutionalBoxValidation,
    IPublicationChannelBoxValidation,
    PublicationAdvancedBox,
    PublicationChannelBox,
    PublicationTopicsBox
} from "views/knowledge/publication/creation/components";
import { IStickerBoxValidation } from 'views/knowledge/publication/creation/components/PublicationStickerBox';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IValidation {
    title?: boolean;
    topics?: boolean;
}

interface IState {
    record: GetListPublicationDto;
    validation: IValidation & IInstitutionalBoxValidation & IPublicationChannelBoxValidation & IStickerBoxValidation;
    uploadingStatus: PublicationUploadingStatus;
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps extends ICreatePublicationProps {
}

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

export const CreateList: React.FC<IProps> = (props) => {
    const { onClose } = props;

    const { t } = useTranslation("knowledge");
    const { enqueueSnackbar } = useSnackbar();
    const api = useCoreApi();
    const session = useSession();
    const navigate = useNavigate();

    const [step, setStep] = useState<number>(1);
    const [lastError, setLastError] = useState<any>();
    const [formCycle, setFormCycle] = useState<RequestLifeCycle>(RequestLifeCycle.None);
    const [loadDto, setLoadDto] = useState<GetListPublicationDto>();
    const [recordChanged, setRecordChanged] = useState<boolean>(false);

    const refTitle = useRef<HTMLInputElement | null>(null);
    const config = useRef<PublicationCreationSettingsResponse | null>(null);

    const steps = [];
    const StepFirstPage = 1;
    const StepSecondPage = 2;
    const StepThirdPage = 3;

    const [state, setState, refState] = useReferredState<IState>(
        {
            record: {
                publicationId: NIL,
                content: "",
                title: "",
                topics: [],
                attachs: [],
                channel: null!,
                isPublicList: true,
                isPrivate: false,
                advancedOptions: {
                    isInstitutional: false,
                    viewPeriodStart: null,
                    viewPeriodEnd: null,
                    viewStickerUntil: null,
                    sticker: null
                },
                allowCollaboration: false,
                openInPlayback: true,
                thumbnail: null,
                collaborators: [],
                publicationType: PublicationType.List,
                firstImageAttach: null,
                presentation: null,
                hasExclusiveItems: false
            },
            validation: {
                title: undefined,
                channel: undefined,
                topics: undefined,
                period: true,
                viewStickerUntil: true
            },

            uploadingStatus: "none",
        });

    ///
    const updateRecord = (data: Partial<GetListPublicationDto>) => {
        return { ...refState.current.record, ...data };
    };

    ///
    const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        var v = event.target.value;
        setState({
            ...state,
            record: updateRecord({ title: v }),
            validation: { ...state.validation, title: v.length > 0 }
        });
    };

    ///
    const handleContentChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        var v = event.target.value;
        setState({
            ...state,
            record: updateRecord({ content: v })
        });
    };

    ///
    const handleIsPublicChange = (checked: boolean) => {
        setState({
            ...state,
            record: updateRecord({ isPublicList: checked, openInPlayback: checked })
        });
    };

    ///
    const handleOpenInPlayback = (checked: boolean) => {
        setState({
            ...state,
            record: updateRecord({ openInPlayback: checked })
        });
    };

    ///
    const handleAllowColaborationChange = (checked: boolean) => {
        const { record } = state;

        setState({
            ...state,
            record: updateRecord({ allowCollaboration: checked, collaborators: checked ? [] : record.collaborators })
        });
    };

    ///
    const handleImageChange = (v: any) => {
        setState({
            ...state,
            record: updateRecord({ thumbnail: v }),
            uploadingStatus: "none"
        });
    };

    ///
    const handleStartUpload = () => {
        setState({
            ...state,
            uploadingStatus: "uploading"
        });
    };

    ///
    const handleCollaborators = (collaborators: MemberAutoCompleteDataModelDto[]) => {
        setState({
            ...state,
            record: updateRecord({ collaborators })
        });
    };

    ///
    const setDefaultValues = () => {
        if (config.current && config.current.defaultChannel.length > 0) {
            const item = config.current.defaultChannel[0];
            setState({
                ...state,
                record: updateRecord({ channel: { channelId: item.key, name: item.label, isPrivate: item.isPrivate, thumbnailUrl: item.thumbnailUrl! }, isPrivate: false, openInPlayback: true }),
                validation: { ...state.validation, channel: true }
            });
        }
        if (config.current && config.current.topics.length > 0) {
            setState({
                ...state,
                record: updateRecord({ topics: config.current.topics, isPrivate: false, openInPlayback: true }),
            });
        }
    };

    ///
    const loadSettings = async () => {
        setFormCycle(RequestLifeCycle.Loading);

        api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: props.channelId ?? null, defaultTopicId: props.topicId ?? null })
            .then((result) => {
                config.current = result.data;
                setDefaultValues();
                setFormCycle(RequestLifeCycle.None);
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const load = async (publicationId: string) => {
        setFormCycle(RequestLifeCycle.Loading);

        Promise.all([
            api.v1.knowledgePublication.getCreationSettings({ defaultChannelId: null, defaultTopicId: props.topicId ?? null }),
            api.v1.knowledgePublication.getListPublication({ publicationId }),
        ])
            .then((results) => {
                config.current = results[0].data;

                const record = results[1].data;

                setState({
                    uploadingStatus: "none",
                    record,
                    validation: {
                        channel: true,
                        topics: true,
                        title: true,
                    }
                });
                setLoadDto(LibUtils.deepCopy(record));
                setFormCycle(RequestLifeCycle.None);
            })
            .catch((error: any) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.LoadFailed);
            });
    };

    ///
    const save = () => {
        const { record } = state;

        const dto: CreateOrUpdateListCommand = {
            publicationId: record.publicationId,
            isPublicList: record.isPublicList,
            title: record.title!,
            content: record.content,
            topics: record.topics.map((item) => item.key),
            channelId: record.channel?.channelId ?? null,
            isPrivate: record.isPrivate,
            advancedOptions: { ...record.advancedOptions, stickerId: null },
            allowCollaboration: record.allowCollaboration,
            collaborators: record.collaborators.map(x => {
                return { memberId: x.key, memberType: x.memberType };
            }),
            contentUnformatted: "",
            thumbnailId: record.thumbnail?.fileId ?? null,
            openInPlayback: record.openInPlayback,
            threadId: null
        };

        setFormCycle(RequestLifeCycle.Posting);

        api.v1.knowledgePublication.createOrUpdateListPublication(dto)
            .then((response) => {
                setFormCycle(RequestLifeCycle.None);

                if (response.data.newPublicationId) {
                    if (response.data.approvalStatus == PublicationApprovalStatus.Approved) {
                        publish("PublicationCreated", response.data.newPublicationId);
                        publish("ListCreated", response.data.newPublicationId);
                        publish("AuthorNewPublication", session.user.userId);
                        enqueueSnackbar(t("Publication.CreateList.PublishMessage"), { variant: "success" });

                        navigate(KnowledgePaths.FeedList(response.data.newPublicationId));

                    } else {
                        enqueueSnackbar(t("Publication.CreateList.PublishModerationMessage"), { variant: "warning" });
                    }
                }
                else {
                    if (response.data.approvalStatus == PublicationApprovalStatus.Approved) {
                        publish("PublicationUpdate", record.publicationId);
                        enqueueSnackbar(t("Publication.CreateList.PublishUpdateMessage"), { variant: "success" });
                    }
                    else {
                        enqueueSnackbar(t("Publication.CreateList.PublishModerationUpdateMessage"), { variant: "warning" });
                    }
                }

                onClose(null);
            })
            .catch((error) => {
                setLastError(error);
                setFormCycle(RequestLifeCycle.PostFailed);
            });
    };

    ///
    const canGoStep2 = () => {
        const { validation, uploadingStatus } = state;
        return uploadingStatus == "uploading"
            ? false
            : (validation.title);
    };

    ///
    const canGoStep3 = () => {
        const { validation, uploadingStatus } = state;
        return uploadingStatus == "uploading"
            ? false
            : (validation.channel && (validation.viewStickerUntil == undefined || validation.viewStickerUntil == true));
    };

    ///
    const canSavePrivate = () => {
        const { validation, uploadingStatus } = state;
        return uploadingStatus == "uploading"
            ? false
            : validation.title && recordChanged;
    };

    ///
    const canSave = (): boolean => {
        const { validation, uploadingStatus } = state;

        return uploadingStatus == "uploading"
            ? false
            : validation.title === true && (validation.viewStickerUntil == undefined || validation.viewStickerUntil == true) && recordChanged;
    };

    ///
    const getSaveButton = () => {
        return (
            <MprcButton
                variant="contained"
                color="success"
                onClick={() => save()}
                disabled={!canSave()}
            >
                <span>{t("Publication.Common.Form.ButtonFinishText")}</span>
            </MprcButton>
        );
    };


    ///
    const getAddPublicationButton = () => {
        return (
            <MprcButton
                variant="contained"
                onClick={() => save()}
                disabled={!canSavePrivate()}
            >
                <span>{t("Publication.CreateList.AddPublicationButtonText")}</span>
            </MprcButton>
        );
    };

    ///
    const getContinueButton = () => {
        return (
            <MprcButton
                variant="contained"
                color="primary"
                disabled={!canGoStep2()}
                onClick={() => setStep(2)} >
                <span>{t("Publication.Common.Form.ButtonContinueText")}</span>
            </MprcButton>
        );
    };

    ///////////////////////////////////////////////////////////////////////
    /// Passos do Formulário
    ///////////////////////////////////////////////////////////////////////

    ///
    steps[StepFirstPage] = () => {
        const { record, validation } = state;

        return (
            <>
                <DialogContent>
                    <Grid container spacing={2}>
                        <Grid alignSelf="flex-start" justifySelf="center">
                            <MprcImageSelection
                                allowEdit
                                enableClear
                                initialSource="local"
                                backgroundType="solid"
                                selectButtomText={t("Publication.CreateList.SelectButtomText")}
                                emptyImageUrl={AppImages.KnowledgeListThumbnail}
                                currentImageUrl={record.thumbnail?.url}
                                acceptedFiles={MediaUtils.DropzoneAcceptImages()}
                                imageDimensions={AppImageDimension.ThumbnailLg}
                                onClear={() => { handleImageChange(null); }}
                                onFileUploadSuccess={(data) => {
                                    handleImageChange(data.thumbnail ? data.thumbnail : data.mainFile);
                                    return true;
                                }}
                                onFileUploadStarted={() => handleStartUpload()}
                            />
                        </Grid>
                        <Grid size="grow">
                            <Grid container direction="column">
                                <Grid size="grow">
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        name="title"
                                        placeholder={t("Publication.CreateList.TitlePlaceholder")}
                                        autoComplete="off"
                                        inputRef={refTitle}
                                        value={record.title}
                                        error={validation.title === false}
                                        slotProps={{
                                            input: {
                                                inputProps: { maxLength: 255 }
                                            }
                                        }}
                                        onChange={(e) => { handleTitleChange(e); }}
                                    />
                                </Grid>
                                <Grid size="grow">
                                    <TextField
                                        fullWidth
                                        multiline
                                        name="title"
                                        placeholder={t("Publication.CreateList.DescriptionPlaceholder")}
                                        value={record.content}
                                        rows={4}
                                        slotProps={{
                                            input: {
                                                inputProps: { maxLength: 1500 }
                                            }
                                        }}
                                        onChange={(e) => { handleContentChange(e); }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                    <Box marginTop={2}>
                        <Grid container spacing={2}>
                            <Grid size={{ xs: 6 }}>
                                <MprcSwitch
                                    label={t("Publication.CreateList.IsPublicLabel")}
                                    disabled={state.record.hasExclusiveItems}
                                    checked={state.record.isPublicList}
                                    onChange={(e) => handleIsPublicChange(e.target.checked)}
                                />
                            </Grid>
                            {state.record.isPublicList &&
                                <Grid size={{ xs: 6 }}>
                                    <MprcSwitch
                                        label={t("Publication.CreateList.OpenPlaybackLabel")}
                                        checked={state.record.openInPlayback}
                                        onChange={(e) => handleOpenInPlayback(e.target.checked)}
                                    />
                                </Grid>
                            }
                        </Grid>
                    </Box>

                </DialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" width="100%">
                        <Grid>
                            {
                                !state.record.isPrivate &&
                                <>
                                    <b>{t("Publication.CreateList.Footer1")}</b>
                                    <span>&nbsp;&raquo;&nbsp;</span>
                                    <span>{t("Publication.CreateList.Footer2")}</span>
                                    <span>&nbsp;&raquo;&nbsp;</span>
                                    <span>{t("Publication.CreateList.Footer3")}</span>
                                </>
                            }
                        </Grid>
                        <Grid>
                            {
                                !record.isPublicList && record.publicationId == NIL
                                    ? getAddPublicationButton()
                                    : !record.isPublicList
                                        ? getSaveButton()
                                        : getContinueButton()
                            }
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };

    ///
    steps[StepSecondPage] = () => {
        const { record, validation } = state;
        return (
            <>
                <DialogContent>

                    <PublicationChannelBox
                        channel={record.channel!}
                        suggestions={config.current!.channelSuggestions}
                        onUpdateState={
                            ({ channel, validation }) => setState({
                                ...state,
                                record: updateRecord({ channel: channel!, collaborators: [] }),
                                validation: { ...state.validation, ...validation }
                            })
                        }
                        validation={validation}
                    />

                    <PublicationTopicsBox
                        channelId={record.channel?.channelId}
                        topics={record.topics as AutoCompleteHierarchicalModel[]}
                        suggestions={[]}
                        publication={{ title: record.title, content: record.content }}
                        onUpdateState={
                            (topics) => setState({
                                ...state,
                                record: updateRecord({ topics: topics as TopicAutoCompleteHierarchicalModelDto[] })
                            })
                        }
                    />

                    <PublicationAdvancedBox
                        channelId={record.channel?.channelId}
                        allowInstitutionalPublisher={config.current?.allowInstitutionalPublisher ?? false}
                        onUpdateState={({ advancedOptions, validation }) => {
                            setState({
                                ...state,
                                record: updateRecord({ advancedOptions: { ...advancedOptions } }),
                                validation: { ...state.validation, ...validation }
                            });
                        }}
                        advancedOptions={{ ...record.advancedOptions }}
                        validation={validation}
                    />

                </DialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={2} width="100%">
                        <Grid>
                            <span>{t("Publication.CreateList.Footer1")}</span>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <b>{t("Publication.CreateList.Footer2")}</b>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <span>{t("Publication.CreateList.Footer3")}</span>
                        </Grid>

                        <Grid>
                            <MprcButton
                                variant="contained"
                                color="cancel"
                                onClick={() => setStep(1)}
                                sx={{ mr: 1 }}
                            >
                                <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                            </MprcButton>

                            <MprcButton
                                variant="contained"
                                color="primary"
                                disabled={!canGoStep3()}
                                onClick={() => setStep(3)} >
                                <span>{t("Publication.Common.Form.ButtonContinueText")}</span>
                            </MprcButton>

                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };


    steps[StepThirdPage] = () => {
        const { record } = state;

        return (
            <>
                <DialogContent>

                    <MprcContainer>
                        <Typography variant="button">{t("Publication.CreateList.ColaborationSubtitle")}</Typography>

                        <MemberAutoComplete
                            multiSelect={true}
                            selectedItems={record.collaborators}
                            placeholder={t("Publication.CreateList.CollaboratorsPlaceholder")}
                            disabled={state.record.allowCollaboration}
                            onChange={(items) => handleCollaborators(items as MemberAutoCompleteDataModelDto[])}
                            onError={error => setLastError(error)}
                            onGetDataCustom={({ searchText, pageNumber, pageSize }, signal) =>
                                new Promise<AutoCompleteSuggestions<MemberAutoCompleteDataModelDto>>((resolve, reject) => {
                                    api.v1.suggestions.channelMember({
                                        searchText: searchText ?? null,
                                        pageNumber,
                                        pageSize,
                                        channelId: record.channel!.channelId
                                    }, { signal })
                                        .then(result => {
                                            resolve(result.data);
                                        })
                                        .catch(e => reject(e));
                                })
                            }
                        />
                        <FormHelperText>
                            {t("Publication.CreateList.ColaborationHintL1")}
                        </FormHelperText>
                        <FormHelperText>
                            {t("Publication.CreateList.ColaborationHintL2")}
                        </FormHelperText>

                        <Box marginTop={2}>
                            <MprcSwitch
                                label={t("Publication.CreateList.AllowCollaborationLabel")}
                                checked={state.record.allowCollaboration}
                                onChange={(e) => handleAllowColaborationChange(e.target.checked)}
                            />
                        </Box>

                    </MprcContainer>

                </DialogContent>

                <DialogActions>
                    <Grid container justifyContent="space-between" alignItems="center" spacing={2} width="100%">
                        <Grid>
                            <span>{t("Publication.CreateList.Footer1")}</span>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <span>{t("Publication.CreateList.Footer2")}</span>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <b>{t("Publication.CreateList.Footer3")}</b>
                        </Grid>

                        <Grid>
                            <MprcButton
                                variant="contained"
                                color="cancel"
                                onClick={() => setStep(2)}
                                sx={{ mr: 1 }}
                            >
                                <span>{t("Publication.Common.Form.ButtonBackText")}</span>
                            </MprcButton>

                            <MprcButton
                                variant="contained"
                                color={
                                    props.publicationId == null || props.publicationId == undefined
                                        ? "primary"
                                        : "success"
                                }
                                onClick={() => save()}
                                disabled={!canSave()}
                            >
                                <span>{
                                    props.publicationId == null || props.publicationId == undefined
                                        ? t("Publication.CreateList.AddPublicationButtonText")
                                        : t("Publication.Common.Form.ButtonFinishText")
                                }
                                </span>
                            </MprcButton>
                        </Grid>
                    </Grid>
                </DialogActions>
            </>
        );
    };


    ///////////////////////////////////////////////////////////////////////
    /// Effect's
    ///////////////////////////////////////////////////////////////////////

    useEffect(() => {
        if (props.publicationId)
            load(props.publicationId);
        else
            loadSettings();
    }, []);

    useEffect(() => {
        const isRecordChanged = !LibUtils.deepCompare(state.record, loadDto);
        setRecordChanged(isRecordChanged);
    }, [state]);

    ///////////////////////////////////////////////////////////////////////
    /// Render
    ///////////////////////////////////////////////////////////////////////

    return (
        <Dialog
            open={true}
            maxWidth={"md"}
            fullWidth
        >
            <DialogTitle>
                <Grid container justifyContent="space-between">
                    <Grid>
                        <Typography variant="h4">
                            {t(state.record.publicationId == NIL ? "Publication.CreateList.TitleNew" : "Publication.CreateList.TitleEdit")}
                        </Typography>
                    </Grid>
                    <Grid>
                        <IconButtonClose
                            color="inherit"
                            size="small"
                            onClick={() => onClose(null)}
                        />
                    </Grid>
                </Grid>
            </DialogTitle>

            <MprcRequestIndicator type="request" visible={formCycle == RequestLifeCycle.Loading} />

            <MprcRequestIndicator type="post" visible={formCycle == RequestLifeCycle.Posting} />

            {
                lastError && formCycle == RequestLifeCycle.LoadFailed
                    ? <MprcRequestErrorReport error={lastError} />
                    : (
                        <>
                            <MprcRequestErrorDialog error={lastError} onClose={() => setLastError(null)} />
                            {steps[step]()}
                        </>
                    )
            }

        </Dialog>
    );
};
